import {
  BaseColor,
  PaletteColor,
  Spacing,
  ThemeColor,
  Tint,
} from '@thiememeulenhoff/tokens';
import { CSSResult, css, unsafeCSS } from 'lit';

// TODO: move these types to token library
export type ThemeTypographyNamespace =
  | 'label'
  | 'nav'
  | 'body'
  | 'heading'
  | 'display';
export type ThemeTypographySize = 'small' | 'medium' | 'large';
export type ThemeTypographyProperty =
  | 'size'
  | 'weight'
  | 'tracking'
  | 'line-height'
  | 'tablet-size';
export type ThemeTypographyWeight = 'weight' | 'semi-bold' | 'bold' | 'light';

export type BorderStyle = 'solid' | 'dashed' | 'dotted';

export const BASE_FONT_SIZE = 16;

/**
 * Transform given pixel value(s) to rem value(s)
 *
 * @param px Array of numbers representing pixel values
 * @returns string of rem values
 */
export const rem = (...px: Array<number>) =>
  unsafeCSS(
    px.reduce((acc, value) => `${acc} ${value / BASE_FONT_SIZE}rem`, '').trim(),
  );

/**
 * Get a prefixed token by name
 *
 * @param name token name without any prefixes
 * @returns a valid prefixed token, i.e. `--tm-base-font-family`
 */
export const tokenName = (name: string) => {
  return unsafeCSS(`--tm-${name}`);
};

/**
 * Get a css var declaration with a prefixed token by name
 *
 * @param name token name without any prefixes
 * @returns a valid prefixed token, i.e. `--tm-base-font-family`
 */
export const token = (name: string) => {
  return css`var(${tokenName(name)})`;
};

/**
 * Get a typography token
 *
 * @param namespace `ThemeTypographyNamespace`
 * @param size `ThemeTypographySize`
 * @param property `ThemeTypographyProperty`
 * @returns a typography token, i.e. `--tm-typography-body-small-line-height`
 */
export const typography = (
  namespace: ThemeTypographyNamespace,
  size: ThemeTypographySize,
  property: ThemeTypographyProperty,
) =>
  unsafeCSS(`var(${tokenName(`typography-${namespace}-${size}-${property}`)})`);

/**
 * Get a css color value for a theme color
 *
 * @param colorName `ThemeColor | PaletteColor | 'black' | 'white`
 * @param colorTint `ThemeColorTint`
 * @returns a var declaration for a color token, i.e. `var(--tm-color-alpha-500)`
 */
export const color = (
  colorName: ThemeColor | PaletteColor | BaseColor,
  colorTint: Tint = 500,
) => {
  if (['white', 'black'].includes(colorName)) {
    return unsafeCSS(`var(${tokenName(`color-${colorName}`)})`);
  }

  const customProperty = tokenName(`color-${colorName}-${colorTint}`);
  const hslColor = `var(${customProperty})`;
  return unsafeCSS(hslColor);
};

/**
 * Get a css color value for tint
 *
 * @param tint `ThemeColorTint`
 * @returns an hsl color for a tint, i.e. `hsl(var(--tm-color-tint-500))`
 */
export const tint = (tint: Tint) => {
  return color('neutral', tint);
};

/**
 * Make the given color transparent.
 *
 * @param color `CSSResult` The color to make transparent
 * @param transparency `number` The transparency value
 * @returns an hsl color with transparency, i.e. `hsl(from var(--tm-color-primary-500) h s l / 50%)`
 */
export const transparent = (color: CSSResult, transparency: number) => {
  return unsafeCSS(css`hsl(from ${color} h s l / ${transparency}%)`);
};

/**
 * Get the full value for a border
 *
 * @param size `number` representing pixel value
 * @param colorName `ThemeColor | 'black' | 'white`
 * @param colorTint `Tint`
 * @param borderStyle `BorderStyle`
 * @returns valid border value, i.e. `1px solid hsl(var(--tm-color-primary-500))`
 */
export const border = (
  size: number,
  colorName: ThemeColor | PaletteColor = 'neutral',
  colorTint: Tint = 500,
  borderStyle: BorderStyle = 'solid',
) => css`
  ${rem(size)} ${unsafeCSS(borderStyle)} ${color(colorName, colorTint)}
`;

/**
 * Set vertical spacing using bottom margin on all but the last child
 *
 * @param spacing `number` representing pixel value
 * @returns css to set bottom margin on all but the last child
 */
export const verticalSpacing = (spacing = 16) => css`
  :host(:not(:last-child)) {
    margin-block-end: ${rem(spacing)};
  }
`;

/**
 * Get typography styles for a given namespace and size
 *
 * @param namespace `ThemeTypographyNamespace`
 * @param size `ThemeTypographySize`
 * @returns full css set for setting text styles for a given namespace and size
 */
export const text = (
  namespace: ThemeTypographyNamespace,
  size: ThemeTypographySize = 'medium',
  weight: ThemeTypographyWeight = 'weight',
) => {
  const baseCSS = css`
    font-size: ${typography(namespace, size, 'size')};
    letter-spacing: ${typography(namespace, size, 'tracking')};
    line-height: ${typography(namespace, size, 'line-height')};
  `;

  let weightCSS = css`
    font-weight: ${typography(namespace, size, 'weight')};
  `;
  switch (weight) {
    case 'bold':
      weightCSS = css`
        font-weight: bold;
      `;
      break;
    case 'light':
      weightCSS = css`
        font-weight: 300;
      `;
      break;
    case 'semi-bold':
      weightCSS = css`
        font-weight: 600;
      `;
      break;
    default:
      break;
  }

  let mediaCSS = css``;
  if (namespace === 'display') {
    mediaCSS = css`
      @media screen and (max-width: 1024px) {
        font-size: ${typography(namespace, size, 'tablet-size')};
      }
    `;
  }

  return css`
    ${baseCSS}
    ${weightCSS}
    ${mediaCSS}
  `;
};

export const display = (size: ThemeTypographySize = 'medium') => {
  return text('display', size);
};

export const heading = (size: ThemeTypographySize = 'medium') => {
  return text('heading', size);
};

export const nav = (
  size: ThemeTypographySize = 'medium',
  weight: ThemeTypographyWeight = 'weight',
) => {
  return text('nav', size, weight);
};

export const body = (
  size: ThemeTypographySize = 'medium',
  weight: ThemeTypographyWeight = 'weight',
) => {
  return text('body', size, weight);
};

export const label = (
  size: ThemeTypographySize = 'small',
  weight: ThemeTypographyWeight = 'weight',
) => {
  return text('label', size, weight);
};

/**
 * Get css to apply styling on only not-last-child items of those selectors
 *
 * @param selectors list of selectors
 * @param styles `CSSResult` to apply
 * @returns full css result to apply styles to not-last-children
 */
export const whereNotLastChild = (
  selectors: Array<string>,
  styles: CSSResult,
) => {
  const whereSelectors = selectors
    .map((selector) => {
      return `${selector}:not(:last-child)`;
    })
    .join(',');

  return css`
    :where(${unsafeCSS(whereSelectors)}) {
      ${styles}
    }
  `;
};

export const spacing = (...spacing: Array<Spacing>) => {
  return unsafeCSS(
    spacing
      .reduce(
        (acc, value) => `${acc} var(${tokenName(`spacing-space-${value}`)})`,
        '',
      )
      .trim(),
  );
};

export const boxShadow = (
  x: number,
  y: number,
  blur: number,
  spread: number,
  shadowColor: CSSResult,
) =>
  unsafeCSS(`${rem(x)} ${rem(y)} ${rem(blur)} ${rem(spread)} ${shadowColor}`);
